import React from 'react';
import { StyleSheet, css } from 'aphrodite';
import { motion } from 'framer-motion';
import {
  Typography,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';

// Updated validation schema to match backend fields
const validationSchema = Yup.object().shape({
  date_of_treatment: Yup.date()
    .required('Treatment date is required')
    .max(new Date(), 'Treatment date cannot be in the future'),
  doctor_name: Yup.string()
    .required('Doctor name is required')
    .max(100, 'Doctor name must be less than 100 characters'),
  medication_prescribed: Yup.string()
    .required('Medication information is required')
    .min(3, 'Medication details must be at least 3 characters'),
  notes: Yup.string().max(500, 'Notes must be less than 500 characters')
});

const TreatmentForm = ({ initialData, onSubmit, onCancel }) => {
  // Get loading and error states from Redux store
  const { loading, error } = useSelector(
    (state) => state.hospitalTreatmentRecords
  );

  // Map initialData to form fields, accounting for field name differences
  const initialValues = initialData
    ? {
        date_of_treatment: initialData.date_of_treatment,
        doctor_name: initialData.doctor_name || '',
        medication_prescribed: initialData.medication_prescribed || '',
        notes: initialData.notes || ''
      }
    : {
        date_of_treatment: new Date().toISOString().split('T')[0],
        doctor_name: '',
        medication_prescribed: '',
        notes: ''
      };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={css(styles.formContainer)}
    >
      <div className={css(styles.formHeader)}>
        <Typography variant='h6' className={css(styles.formTitle)}>
          {initialData ? 'Edit Treatment Record' : 'Add New Treatment Record'}
        </Typography>
        <IconButton onClick={onCancel} size='small' disabled={loading}>
          <Close />
        </IconButton>
      </div>

      <Divider className={css(styles.divider)} />

      {/* Show error message if exists */}
      {error && (
        <Alert severity='error' className={css(styles.alert)}>
          {typeof error === 'object' ? JSON.stringify(error) : error}
        </Alert>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, errors, touched, isValid, dirty, handleSubmit }) => (
          <Form className={css(styles.form)}>
            <div className={css(styles.fieldGroup)}>
              <label htmlFor='date_of_treatment' className={css(styles.label)}>
                Treatment Date *
              </label>
              <Field
                type='date'
                id='date_of_treatment'
                name='date_of_treatment'
                className={css(
								  styles.input,
								  touched.date_of_treatment &&
										errors.date_of_treatment &&
										styles.inputError
                )}
                disabled={loading}
              />
              <ErrorMessage
                name='date_of_treatment'
                component='div'
                className={css(styles.errorMessage)}
              />
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='doctor_name' className={css(styles.label)}>
                Doctor Name *
              </label>
              <Field
                type='text'
                id='doctor_name'
                name='doctor_name'
                placeholder='e.g., Dr. John Smith'
                className={css(
								  styles.input,
								  touched.doctor_name &&
										errors.doctor_name &&
										styles.inputError
                )}
                disabled={loading}
              />
              <ErrorMessage
                name='doctor_name'
                component='div'
                className={css(styles.errorMessage)}
              />
            </div>

            <div className={css(styles.fieldGroup)}>
              <label
                htmlFor='medication_prescribed'
                className={css(styles.label)}
              >
                Prescribed Medication *
              </label>
              <Field
                type='text'
                id='medication_prescribed'
                name='medication_prescribed'
                placeholder='e.g., Amoxicillin 500mg, 3 times daily for 7 days'
                className={css(
								  styles.input,
								  touched.medication_prescribed &&
										errors.medication_prescribed &&
										styles.inputError
                )}
                disabled={loading}
              />
              <ErrorMessage
                name='medication_prescribed'
                component='div'
                className={css(styles.errorMessage)}
              />
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='notes' className={css(styles.label)}>
                Notes (Optional)
              </label>
              <Field
                as='textarea'
                id='notes'
                name='notes'
                placeholder='Additional details about the treatment'
                className={css(
								  styles.textarea,
								  touched.notes && errors.notes && styles.inputError
                )}
                rows={4}
                disabled={loading}
              />
              <ErrorMessage
                name='notes'
                component='div'
                className={css(styles.errorMessage)}
              />
            </div>

            <div className={css(styles.formActions)}>
              <Button
                type='button'
                variant='outlined'
                onClick={onCancel}
                className={css(styles.cancelButton)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='primary'
                disabled={loading || !(isValid && dirty)}
                className={css(styles.submitButton)}
                startIcon={
									loading && <CircularProgress size={20} color='inherit' />
								}
              >
                {loading
								  ? initialData
								    ? 'Updating...'
								    : 'Saving...'
								  : initialData
								    ? 'Update Record'
								    : 'Save Record'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    width: '100%'
  },
  formHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem'
  },
  formTitle: {
    color: '#2783F7',
    fontWeight: 'bold'
  },
  divider: {
    margin: '1rem 0'
  },
  alert: {
    marginBottom: '1rem'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  label: {
    fontWeight: 'bold',
    fontSize: '0.9rem',
    color: '#333'
  },
  input: {
    padding: '0.75rem',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '1rem',
    ':focus': {
      outline: 'none',
      borderColor: '#2783F7',
      boxShadow: '0 0 0 2px rgba(39, 131, 247, 0.2)'
    },
    ':disabled': {
      backgroundColor: '#f5f5f5',
      cursor: 'not-allowed'
    }
  },
  textarea: {
    padding: '0.75rem',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '1rem',
    resize: 'vertical',
    ':focus': {
      outline: 'none',
      borderColor: '#2783F7',
      boxShadow: '0 0 0 2px rgba(39, 131, 247, 0.2)'
    },
    ':disabled': {
      backgroundColor: '#f5f5f5',
      cursor: 'not-allowed'
    }
  },
  inputError: {
    borderColor: '#f44336',
    ':focus': {
      boxShadow: '0 0 0 2px rgba(244, 67, 54, 0.2)'
    }
  },
  errorMessage: {
    color: '#f44336',
    fontSize: '0.8rem'
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    marginTop: '1rem'
  },
  cancelButton: {
    color: '#555',
    borderColor: '#ccc'
  },
  submitButton: {
    backgroundColor: '#2783F7',
    '&:hover': {
      backgroundColor: '#1b6ed9'
    },
    minWidth: '120px'
  }
});

export default TreatmentForm;
